import { FileSearch, Loader2, BarChart3, X, Target, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const AnalyzerTab = ({
  competitorUrl, setCompetitorUrl,
  analyzing, competitorAnalysis,
  onAnalyzeCompetitor, onUsePost,
  credits, ANALYZER_COST
}) => (
  <div className="space-y-6">
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600">
            <FileSearch className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Competitor Analyzer</h2>
            <p className="text-gray-500 text-sm">Analyze competitor websites and get AI-generated posts to beat them</p>
          </div>
        </div>
        <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm font-medium">{ANALYZER_COST} credits</span>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="text-gray-700 dark:text-gray-300 font-medium">Competitor Website URL</Label>
          <Input 
            placeholder="https://competitor-website.com"
            value={competitorUrl}
            onChange={(e) => setCompetitorUrl(e.target.value)}
            className="mt-2"
          />
        </div>
        <Button 
          onClick={onAnalyzeCompetitor}
          disabled={analyzing || credits < ANALYZER_COST}
          className="w-full bg-gradient-to-r from-amber-500 to-orange-600 py-6 text-lg"
        >
          {analyzing ? (
            <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Analyzing Competitor...</>
          ) : (
            <><FileSearch className="h-5 w-5 mr-2" />Analyze & Generate Beat Post ({ANALYZER_COST} credits)</>
          )}
        </Button>
      </div>

      {competitorAnalysis && (
        <div className="mt-6 space-y-4">
          <div className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
            <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-amber-600" />
              Analysis: {competitorAnalysis.domain}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {[
                { label: "Est. Traffic", value: competitorAnalysis.metrics.estimated_traffic },
                { label: "Followers", value: competitorAnalysis.metrics.social_followers },
                { label: "Post Freq", value: competitorAnalysis.metrics.post_frequency },
                { label: "Engagement", value: competitorAnalysis.metrics.avg_engagement },
              ].map(({ label, value }) => (
                <div key={label} className="bg-white p-3 rounded-lg text-center">
                  <div className="text-lg font-bold text-gray-800 dark:text-white">{value}</div>
                  <div className="text-xs text-gray-500">{label}</div>
                </div>
              ))}
            </div>
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Weaknesses Found:</h4>
              <ul className="space-y-1">
                {competitorAnalysis.weaknesses.map((w, i) => (
                  <li key={i} className="text-sm text-red-600 flex items-center gap-2">
                    <X className="h-3 w-3" /> {w}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
            <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <Target className="h-5 w-5 text-green-600" />
              Beat Them With This Post
            </h3>
            <div className="flex gap-4">
              <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                <img src={competitorAnalysis.beatThemPost.image_url} alt="Beat post" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <p className="text-gray-800 dark:text-white mb-2">{competitorAnalysis.beatThemPost.caption}</p>
                <p className="text-sm text-blue-600 mb-3">{competitorAnalysis.beatThemPost.hashtags}</p>
                <Button size="sm" className="bg-green-600" onClick={() => onUsePost(competitorAnalysis.beatThemPost)}>
                  <Sparkles className="h-3 w-3 mr-1" />
                  Generate This Post
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);
